// Phase 48 — soft conflict warning modal. Triggered by QuickBookingForm /
// BookingForm / EnquiryDialog / Calendar drag-drop *before* committing
// the create/reschedule call. Never blocks the manager — Save anyway is
// always an option.
//
// Each conflict row exposes the offending booking's code as a clickable
// link → opens that booking in a new tab via the calendar deep-link so
// the manager can verify the overlap without losing the half-filled form.
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Users, User, Route, ExternalLink } from "lucide-react";

function iconFor(type) {
  if (type === "staff") return <User className="h-4 w-4" />;
  if (type === "customer") return <Users className="h-4 w-4" />;
  if (type === "travel") return <Route className="h-4 w-4" />;
  return <AlertTriangle className="h-4 w-4" />;
}

function labelFor(type) {
  if (type === "staff") return "Staff overlap";
  if (type === "customer") return "Customer overlap";
  if (type === "travel") return "Travel time";
  return "Conflict";
}

export default function ConflictWarningDialog({ open, onOpenChange, conflicts = [], onProceed, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="border-amber-300 bg-amber-50"
        data-testid="conflict-warning-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Time conflict detected
          </AlertDialogTitle>
          <AlertDialogDescription className="text-amber-900/80">
            The booking you{"\u2019"}re about to save overlaps existing slots or is hard to reach in time. You can save anyway if you{"\u2019"}ve already coordinated.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2 my-2 max-h-[260px] overflow-y-auto">
          {conflicts.map((c, i) => (
            <li
              key={`${c.type}-${c.booking_id}-${i}`}
              className="flex items-start gap-2 bg-white border border-amber-200 rounded-md px-3 py-2 text-[13px]"
              data-testid={`conflict-row-${c.type}-${i}`}
            >
              <span className="text-amber-700 mt-0.5 shrink-0">{iconFor(c.type)}</span>
              <div className="min-w-0 flex-1">
                <div className="mono text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                  {labelFor(c.type)}
                </div>
                <div className="text-foreground">{c.message}</div>
                {c.booking_id && c.booking_code && (
                  <a
                    href={`/calendar?bookingId=${encodeURIComponent(c.booking_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 mt-1 text-[11px] mono font-bold text-amber-800 hover:text-amber-900 underline underline-offset-2 decoration-amber-400"
                    data-testid={`conflict-row-link-${c.type}-${i}`}
                  >
                    Open {c.booking_code}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onCancel?.()} data-testid="conflict-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onProceed?.()}
            data-testid="conflict-save-anyway"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Save anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
